import { ArrowSquareOutIcon, CaretDownIcon, LinkBreakIcon, PlusIcon, TrashSimpleIcon } from '@phosphor-icons/react'
import { mappedColors } from '@pixee/design-system'
import * as Dialog from '@radix-ui/react-dialog'
import { type DistributiveOmit } from '@tanstack/react-query'
import classNames from 'classnames'
import { Suspense, useCallback, useMemo, useRef, useState } from 'react'
import { match } from 'ts-pattern'
import { useImmer } from 'use-immer'
import { z } from 'zod'
import { useShowMutationError } from '../../hooks/use-show-mutation-error.tsx'
import { useToastService } from '../../services/toast-service.tsx'
import type { AreEquivalent } from '../../types'
import {
  useDeleteRepositoryIntegration,
  useGetRepository,
  useGetRepositoryIntegrations,
  usePutRepositoryIntegration,
  useSuspenseGetRepositoryIntegrations,
} from '../../utils/api-client/api-hooks'
import type { RepositoryIntegration, Tool } from '../../utils/api-client/api-schemas'
import { getRepositoryProject, Repository } from '../../utils/api-client/api-schemas'
import { getConstraintViolationMessage } from '../../utils/error-handling/utils.ts'
import {
  convertIntegrationTypeToTool,
  getIntegrationTypeName,
  getRepositoryIdentifierLabel,
  getRepositoryName,
  getRepositoryOrganization,
  getRepositoryTypeDisplayName,
  TOOL_INTEGRATION_TYPES,
  type ToolIntegration,
} from '../../utils/repository-utils'
import { GhostButton } from '../buttons/ghost-button'
import { ErrorBoundary } from '../error-boundaries/error-boundary'
import { InputGroup } from '../input/input-group'
import { SelectBox } from '../input/select-box'
import { TextInput } from '../input/text-input'
import { DeleteRepositoryModal } from '../modals/delete-repository-modal.tsx'
import { RemoveIntegrationModal } from '../modals/remove-integration-modal.tsx'
import { SCMIcon } from '../third-party-branding/scm-icon'
import { ToolIcon } from '../third-party-branding/tool-icon'
import { AddFindingsDrawer } from './add-findings-drawer'
import {
  getIsCiRunScanCreationAvailable,
  getIsImportAvailableWithoutMapping,
  getIsMappingResolvedByPlatform,
} from './add-findings-drawer-utils.ts'
import * as styles from './add-findings-drawer.css'
import { CiRunSettingSwitch } from './ci-run-setting.tsx'
import { DrawerRepositoryContext } from './drawer-repository-context.tsx'
import * as footerStyles from './floating-footer.css'
import { FloatingFooter } from './floating-footer.tsx'
import { useIsMapToolFormOffered, useMappableIntegrationTypes } from './map-tool-integration-form.hooks.ts'
import {
  MapToolIntegrationForm,
  useMapToolIntegrationForm,
  type MapToolIntegrationFormValues,
} from './map-tool-integration-form.tsx'
import { usePolarisMappingStatus, type PolarisMappingStatus } from './repository-mapping.hooks.ts'
import * as localStyles from './repository-settings-drawer.css'

const RepositoryTypeSchema = z.enum(['github', 'gitlab', 'azure', 'bitbucket', 'git'])
true satisfies AreEquivalent<z.infer<typeof RepositoryTypeSchema>, Repository['type']>

type RepositorySettingsDrawerProps = {
  repository: DistributiveOmit<Repository, '_links'>
  onCloseDrawer: () => unknown
  onUploadClick: () => unknown
}

export const RepositorySettingsDrawer: React.FC<RepositorySettingsDrawerProps> = ({
  repository,
  onCloseDrawer,
  onUploadClick,
}) => {
  const drawerContentRef = useRef<HTMLDivElement>(null)
  const [showDeleteRepositoryDialog, setShowDeleteRepositoryDialog] = useState(false)
  const [showUploadForm, setShowUploadForm] = useState(false)
  const [mapFormDefaults, setMapFormDefaults] = useState<{ selectedTool?: Tool }>()
  const isMapFormOpen = mapFormDefaults !== undefined
  const [expandedToolIds, updateExpandedToolIds] = useImmer(new Set<string>())
  const [integrationToRemove, setIntegrationToRemove] = useState<string | null>(null)

  // The gate below and the form it opens must agree on whether the form has anything to offer, so
  // both read the same question from the same place: which tools have at least one instance.
  const isMapToolFormOffered = useIsMapToolFormOffered()
  const { data: repositoryIntegrations } = useGetRepositoryIntegrations({ repositoryId: repository.id })
  const polarisMappingStatus = usePolarisMappingStatus(repositoryIntegrations?._embedded.items)
  const unboundTypes = useUnboundIntegrationTypes({
    mappedIntegrations: repositoryIntegrations?._embedded.items,
    repositoryType: repository.type,
  })

  const { addToast } = useToastService()
  const showRemoveIntegrationError = useShowMutationError({ prefix: 'Failed to remove integration' })
  const deleteRepositoryIntegration = useDeleteRepositoryIntegration({
    onSuccess: () => {
      addToast({
        message: <>Integration removed successfully.</>,
        variant: 'success',
      })
    },
    onError: showRemoveIntegrationError,
  })

  const handleRemoveMapping = useCallback((integrationId: string) => {
    setIntegrationToRemove(integrationId)
  }, [])

  const handleConfirmRemoveMapping = useCallback(() => {
    if (integrationToRemove) {
      deleteRepositoryIntegration.mutate({ repositoryId: repository.id, integrationId: integrationToRemove })
      setIntegrationToRemove(null)
    }
  }, [integrationToRemove, deleteRepositoryIntegration, repository.id])

  const handleCancelRemoveMapping = useCallback(() => {
    setIntegrationToRemove(null)
  }, [])

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        onCloseDrawer()
      }
    },
    [onCloseDrawer]
  )

  const handleUploadClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()
      onCloseDrawer()
      onUploadClick()
    },
    [onCloseDrawer, onUploadClick]
  )

  const handleMapNewClick = useCallback(() => {
    setMapFormDefaults({})
  }, [])

  const handleMapUnboundClick = useCallback((type: ToolIntegration['type']) => {
    setMapFormDefaults({ selectedTool: convertIntegrationTypeToTool(type) })
  }, [])

  const handleCloseMapForm = useCallback(() => {
    setMapFormDefaults(undefined)
  }, [])

  const toggleToolExpansion = useCallback((toolId: string) => {
    updateExpandedToolIds(draft => {
      if (draft.has(toolId)) {
        draft.delete(toolId)
      } else {
        draft.add(toolId)
      }
    })
  }, [])

  const repositoryOrganization = getRepositoryOrganization(repository)
  const repositoryName = getRepositoryName(repository)
  // Read here rather than taken from the prop. Embedding is opt-in, so a caller that passes a
  // repository fetched without it leaves this field blank with nothing to say it is missing — which is
  // what happened on the repository details page. A caller that did embed it hits the cache.
  const { data: repositoryWithProject } = useGetRepository({
    repositoryId: repository.id,
    embed: ['project-membership.project'],
  })
  // Named "Pixee project" rather than "Project": an Azure DevOps repository already shows an Azure
  // project in this same group, and the two are unrelated.
  const pixeeProject = repositoryWithProject && getRepositoryProject(repositoryWithProject)

  return (
    <>
      <Dialog.Root open={true} modal={false} onOpenChange={handleOpenChange}>
        <Dialog.Portal>
          <Dialog.Content
            ref={drawerContentRef}
            className={classNames(styles.content, footerStyles.drawerContentPaddingAdjustment)}
            data-has-floating-footer={true}
            onInteractOutside={event => event.preventDefault()}
          >
            <Dialog.Title className={styles.title}>
              <DrawerRepositoryContext type={repository.type} name={repository.name} />
              Repository settings
            </Dialog.Title>
            <Dialog.Description className={`${styles.description} ${localStyles.separator}`}>
              Configure repository analysis and notification settings
            </Dialog.Description>

            <ErrorBoundary fallbackTitle="Unable to display settings">
              <div style={{ display: 'flex', width: '100%' }} className={localStyles.settingsContainer}>
                <InputGroup title="Connection type">
                  <SelectBox
                    label="Integration"
                    value={repository.type}
                    disabled={true}
                    options={RepositoryTypeSchema.options.map(type => ({
                      value: type,
                      label: (
                        <span className={localStyles.selectItemText}>
                          <SCMIcon type={type} size="small" />
                          {getRepositoryTypeDisplayName(type)}
                        </span>
                      ),
                    }))}
                  />
                  <SelectBox
                    label="Organization"
                    value={repositoryOrganization}
                    disabled={true}
                    options={[
                      {
                        value: repositoryOrganization,
                        label: repositoryOrganization,
                      },
                    ]}
                  />
                  <SelectBox
                    label="Project"
                    value={repositoryName}
                    disabled={true}
                    options={[
                      {
                        value: repositoryName,
                        label: repositoryName,
                      },
                    ]}
                  />
                  {pixeeProject && (
                    <SelectBox
                      label="Pixee project"
                      value={pixeeProject.name}
                      disabled={true}
                      options={[
                        {
                          value: pixeeProject.name,
                          label: pixeeProject.name,
                        },
                      ]}
                    />
                  )}
                </InputGroup>

                <InputGroup
                  title="Mapped tools"
                  titleAction={
                    isMapToolFormOffered ? (
                      <GhostButton
                        type="default"
                        size="small"
                        state={isMapFormOpen ? 'disabled' : 'default'}
                        onClick={handleMapNewClick}
                      >
                        <PlusIcon
                          size={16}
                          style={{
                            marginRight: '4px',
                            color: mappedColors.neutral.halo,
                          }}
                        />
                        Map new
                      </GhostButton>
                    ) : undefined
                  }
                >
                  {mapFormDefaults && (
                    <Suspense fallback={null}>
                      <MapToolIntegrationFormHost
                        repositoryId={repository.id}
                        repositoryType={repository.type}
                        selectedTool={mapFormDefaults.selectedTool}
                        onSuccess={handleCloseMapForm}
                        onCancel={handleCloseMapForm}
                        onUploadClick={handleUploadClick}
                      />
                    </Suspense>
                  )}

                  <MappedToolList
                    repositoryId={repository.id}
                    repositoryIntegrations={repositoryIntegrations?._embedded.items}
                    unboundTypes={unboundTypes}
                    repositoryType={repository.type}
                    polarisMappingStatus={polarisMappingStatus}
                    isMapFormOpen={isMapFormOpen}
                    expandedToolIds={expandedToolIds}
                    onToggleToolExpansion={toggleToolExpansion}
                    onRemoveMapping={handleRemoveMapping}
                    onMapUnbound={handleMapUnboundClick}
                  />
                </InputGroup>
              </div>
            </ErrorBoundary>
          </Dialog.Content>
          <FloatingFooter drawerContentRef={drawerContentRef}>
            <ActionButtons
              onOpenDeleteRepositoryDialog={() => setShowDeleteRepositoryDialog(true)}
              onCloseDrawer={onCloseDrawer}
            />
          </FloatingFooter>
        </Dialog.Portal>
      </Dialog.Root>

      {showDeleteRepositoryDialog && (
        <DeleteRepositoryModal
          repository={repository}
          onCloseDeleteRepositoryDialog={() => setShowDeleteRepositoryDialog(false)}
          onDeleteRepositorySuccess={onCloseDrawer}
        />
      )}

      {showUploadForm && (
        <AddFindingsDrawer repositoryId={repository.id} onCloseDrawer={() => setShowUploadForm(false)} />
      )}

      {integrationToRemove && (
        <RemoveIntegrationModal
          repository={repository}
          repositoryIntegrations={repositoryIntegrations?._embedded.items}
          integrationToRemove={integrationToRemove}
          setIntegrationToRemove={setIntegrationToRemove}
          onCancelRemoveMapping={handleCancelRemoveMapping}
          onConfirmRemoveMapping={handleConfirmRemoveMapping}
        />
      )}
    </>
  )
}

type UseUnboundIntegrationTypesInput = {
  mappedIntegrations: DistributiveOmit<RepositoryIntegration, '_links'>[] | undefined
  repositoryType: Repository['type']
}

const useUnboundIntegrationTypes = ({
  mappedIntegrations,
  repositoryType,
}: UseUnboundIntegrationTypesInput): ToolIntegration['type'][] | undefined => {
  const { types: typesWithInstances, isLoading, isError } = useMappableIntegrationTypes()

  return useMemo(() => {
    // Until the counts arrive, or if they fail, which tools are connected is unknown, and an empty
    // list would read as "No tools detected". The failure itself is reported by the map-new form,
    // which `useIsMapToolFormOffered` keeps offered for that reason.
    if (isLoading || isError) {
      return undefined
    }
    if (mappedIntegrations === undefined) {
      return []
    }
    return TOOL_INTEGRATION_TYPES.filter(
      type =>
        typesWithInstances.includes(type) &&
        !mappedIntegrations.some(integration => integration.type === type) &&
        !getIsMappingResolvedByPlatform(convertIntegrationTypeToTool(type), repositoryType)
    )
  }, [isLoading, isError, mappedIntegrations, typesWithInstances, repositoryType])
}

type MapToolIntegrationFormHostProps = {
  repositoryId: string
  repositoryType: Repository['type']
  selectedTool?: Tool
  onSuccess: () => unknown
  onCancel: () => unknown
  onUploadClick: (e: React.MouseEvent) => unknown
}

const MapToolIntegrationFormHost: React.FC<MapToolIntegrationFormHostProps> = ({
  repositoryId,
  repositoryType,
  selectedTool,
  onSuccess,
  onCancel,
  onUploadClick,
}) => {
  const { data: repositoryIntegrations } = useSuspenseGetRepositoryIntegrations({ repositoryId })

  const formDefaultValues = useMemo<Partial<MapToolIntegrationFormValues> | undefined>(() => {
    if (selectedTool) {
      return { selectedTool }
    }
    const items = repositoryIntegrations._embedded?.items ?? []
    // Unlike the arms below, Sonar's stored key is not prefilled: its field is a picker that shows
    // only a project it listed, so a bare key from the old mapping would sit in the form behind a
    // blank field. Nothing is lost, since saving any prefilled mapping here answers 409 anyway.
    if (items.some(i => i.type === 'sonar')) {
      return { selectedTool: 'sonar' }
    }
    const checkmarx = items.find(i => i.type === 'checkmarx')
    if (checkmarx) {
      return { selectedTool: 'checkmarx', projectKey: checkmarx.project_id }
    }
    const appscan = items.find(i => i.type === 'appscan')
    if (appscan) {
      return { selectedTool: 'appscan', projectKey: appscan.application_id }
    }
    const blackDuckHub = items.find(i => i.type === 'black-duck-hub')
    if (blackDuckHub) {
      return {
        selectedTool: 'black_duck_hub_sca',
        projectKey: blackDuckHub.project_id,
        versionId: blackDuckHub.version_id,
      }
    }
    const checkmarxEnterprise = items.find(i => i.type === 'checkmarx-enterprise')
    if (checkmarxEnterprise) {
      return { selectedTool: 'checkmarx_enterprise', projectKey: String(checkmarxEnterprise.project_id) }
    }
    return undefined
  }, [repositoryIntegrations, selectedTool])

  const form = useMapToolIntegrationForm(formDefaultValues)

  return (
    <form.AppForm>
      <MapToolIntegrationForm
        repositoryId={repositoryId}
        repositoryType={repositoryType}
        onSuccess={onSuccess}
        onCancel={onCancel}
        onUploadClick={onUploadClick}
      />
    </form.AppForm>
  )
}

type MappedToolListProps = {
  repositoryId: string
  repositoryIntegrations?: DistributiveOmit<RepositoryIntegration, '_links'>[]
  /** `undefined` while the connected tools are unknown: no unbound rows, and no "No tools detected". */
  unboundTypes: readonly ToolIntegration['type'][] | undefined
  repositoryType: Repository['type']
  polarisMappingStatus: PolarisMappingStatus | undefined
  isMapFormOpen: boolean
  expandedToolIds: Set<string>
  onToggleToolExpansion: (toolId: string) => unknown
  onRemoveMapping: (integrationId: string) => unknown
  onMapUnbound: (type: ToolIntegration['type']) => unknown
}

const MappedToolList: React.FC<MappedToolListProps> = ({
  repositoryId,
  repositoryIntegrations = [],
  unboundTypes,
  repositoryType,
  polarisMappingStatus,
  isMapFormOpen,
  expandedToolIds,
  onToggleToolExpansion,
  onRemoveMapping,
  onMapUnbound,
}) => {
  if (!repositoryIntegrations.length && !unboundTypes?.length) {
    return unboundTypes === undefined ? null : (
      <div
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
        className={localStyles.noToolsContainer}
        data-testid="no-tools-detected"
      >
        <span className={localStyles.italicText}>No tools detected</span>
      </div>
    )
  }
  return (
    <div className={localStyles.mappedToolsContainer}>
      {repositoryIntegrations.map(integration => (
        <MappedTool
          key={integration.id}
          integration={integration}
          repositoryId={repositoryId}
          repositoryType={repositoryType}
          warning={
            integration.type === 'polaris' ? getPolarisMappingWarning(polarisMappingStatus, repositoryType) : undefined
          }
          expandedToolIds={expandedToolIds}
          onToggleToolExpansion={onToggleToolExpansion}
          onRemoveMapping={onRemoveMapping}
        />
      ))}
      {unboundTypes?.map(type => (
        <UnboundTool
          key={type}
          type={type}
          repositoryType={repositoryType}
          isMapFormOpen={isMapFormOpen}
          onMap={onMapUnbound}
        />
      ))}
    </div>
  )
}

const getPolarisMappingWarning = (
  status: PolarisMappingStatus | undefined,
  repositoryType: Repository['type']
): string | undefined =>
  match(status)
    .with(
      'unbound',
      () => getIsImportAvailableWithoutMapping('polaris_sast', repositoryType),
      () =>
        "Not bound to a managed instance. Pixee imports Polaris SAST findings from this repository's build artifact, but can't import Polaris SCA findings through this mapping. To import them, remove this mapping and map the repository again on a managed instance."
    )
    .with(
      'unbound',
      () =>
        "Not bound to a managed instance, so Pixee can't import Polaris findings through it. Remove this mapping and map the repository again on a managed instance."
    )
    .with(
      'unverified',
      () =>
        "Pixee couldn't load this mapping's Polaris instance, so it can't check whether findings can be imported through it. Reload to try again."
    )
    .with('pending', 'fetchable', undefined, () => undefined)
    .exhaustive()

type MappedToolProps = {
  integration: DistributiveOmit<RepositoryIntegration, '_links'>
  repositoryId: string
  repositoryType: Repository['type']
  warning: string | undefined
  expandedToolIds: Set<string>
  onToggleToolExpansion: (toolId: string) => unknown
  onRemoveMapping: (integrationId: string) => unknown
}

const MappedTool: React.FC<MappedToolProps> = ({
  integration,
  repositoryId,
  repositoryType,
  warning,
  expandedToolIds,
  onToggleToolExpansion,
  onRemoveMapping,
}) => {
  const getIntegrationId = (integration: DistributiveOmit<RepositoryIntegration, '_links'>): string | undefined => {
    return (
      match(integration)
        .with({ type: 'sonar' }, integration => integration.project_key)
        .with({ type: 'checkmarx' }, integration => integration.project_id)
        .with({ type: 'appscan' }, integration => integration.application_id)
        .with({ type: 'veracode' }, integration => integration.application_guid)
        .with({ type: 'arnica' }, integration => integration.asset_key)
        .with({ type: 'datadog' }, integration => integration.repository_id)
        .with({ type: 'polaris' }, integration => integration.polaris_project_id)
        // The project UUID alone, to match the "Project ID" label this feeds. A Hub mapping also
        // carries a version UUID, which this single-identifier row has nowhere to put; the mapping
        // form is where both are seen together.
        .with({ type: 'black-duck-hub' }, integration => integration.project_id)
        .with({ type: 'checkmarx-enterprise' }, integration => String(integration.project_id))
        .exhaustive()
    )
  }

  const tool = convertIntegrationTypeToTool(integration.type)
  const identifierLabel = getRepositoryIdentifierLabel(tool)

  return (
    <div className={localStyles.mappedToolItem}>
      <div
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}
        className={localStyles.mappedToolHeader}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'start', width: '100%' }}>
          <span className={localStyles.mappedToolName}>
            <ToolIcon tool={tool} size="medium" />
            {getIntegrationTypeName(integration.type)}
          </span>
          {'html_url' in integration && integration.html_url ? (
            <a
              href={integration.html_url}
              target="_blank"
              rel="noopener noreferrer"
              className={localStyles.projectKeyText}
            >
              <div className={localStyles.flexCenterGap4}>
                {getIntegrationId(integration)}
                <ArrowSquareOutIcon size={12} weight="light" style={{ color: mappedColors.neutral.neutral10 }} />
              </div>
            </a>
          ) : (
            <span className={localStyles.projectKeyText}>{getIntegrationId(integration)}</span>
          )}
          {warning !== undefined && <span className={localStyles.projectKeyText}>{warning}</span>}
        </div>
        <button
          className={localStyles.caretButton}
          onClick={() => onToggleToolExpansion(integration.id)}
          aria-label="Toggle tool details"
        >
          <CaretDownIcon
            size={16}
            weight="regular"
            style={{
              transform: expandedToolIds.has(integration.id) ? undefined : 'rotate(-90deg)',
            }}
          />
        </button>
      </div>
      {expandedToolIds.has(integration.id) && (
        <div className={localStyles.toolDetails}>
          {/* One field driven by the shared label table and `getIntegrationId`, rather than an arm per
              tool — the per-arm version had no `veracode` case, so an expanded Veracode mapping
              showed no identifier at all even though its GUID was right there. */}
          {identifierLabel !== undefined && (
            <TextInput
              id={`identifier-${integration.id}`}
              label={identifierLabel}
              value={getIntegrationId(integration) ?? ''}
              disabled={true}
            />
          )}
          {integration.type === 'polaris' && getIsCiRunScanCreationAvailable(repositoryType) && (
            <CiRunMappingSetting repositoryId={repositoryId} integrationId={integration.id} />
          )}
          <div style={{ display: 'flex', justifyContent: 'end', width: '100%', marginTop: '24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <LinkBreakIcon size={16} weight="light" style={{ color: mappedColors.neutral.halo }} />
              <span className={localStyles.removeMappingText} onClick={() => onRemoveMapping(integration.id)}>
                Remove mapping
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

type CiRunMappingSettingProps = {
  repositoryId: string
  integrationId: string
}

const CiRunMappingSetting: React.FC<CiRunMappingSettingProps> = ({ repositoryId, integrationId }) => {
  const { data: repositoryIntegrations } = useGetRepositoryIntegrations({ repositoryId })
  const mappings = repositoryIntegrations?._embedded.items
  const mapping = mappings?.find(candidate => candidate.id === integrationId)
  const isCreatingScansOnCiRun = mapping?.type === 'polaris' && mapping.create_scans_on_ci_run
  const isBoundToManagedInstance = usePolarisMappingStatus(mappings) === 'fetchable'

  const { addToast } = useToastService()
  const showUpdateCiRunSettingError = useShowMutationError({
    prefix: 'Failed to update the CI-run setting',
    getMessage: getConstraintViolationMessage,
  })
  const putRepositoryIntegration = usePutRepositoryIntegration({
    onError: showUpdateCiRunSettingError,
    onSuccess: () => addToast({ message: <>CI-run setting saved.</>, variant: 'success' }),
  })

  return (
    <CiRunSettingSwitch
      className={localStyles.ciRunMappingSetting}
      isChecked={isCreatingScansOnCiRun}
      isDisabled={putRepositoryIntegration.isPending || (!isCreatingScansOnCiRun && !isBoundToManagedInstance)}
      onCheckedChange={isChecked =>
        putRepositoryIntegration.mutate({
          repositoryId,
          integrationId,
          body: { type: 'polaris', create_scans_on_ci_run: isChecked },
        })
      }
    />
  )
}

type UnboundToolProps = {
  type: ToolIntegration['type']
  repositoryType: Repository['type']
  isMapFormOpen: boolean
  onMap: (type: ToolIntegration['type']) => unknown
}

const getUnboundDescription = (type: ToolIntegration['type'], repositoryType: Repository['type']): string =>
  match(type)
    .with(
      'polaris',
      () => getIsImportAvailableWithoutMapping('polaris_sast', repositoryType),
      () =>
        "No instance bound. Pixee imports Polaris SAST findings from this repository's build artifact, but can't import Polaris SCA findings."
    )
    .otherwise(
      () => `No instance bound. Pixee can't import ${getIntegrationTypeName(type)} findings for this repository.`
    )

const UnboundTool: React.FC<UnboundToolProps> = ({ type, repositoryType, isMapFormOpen, onMap }) => {
  const tool = convertIntegrationTypeToTool(type)
  const toolName = getIntegrationTypeName(type)

  return (
    <div className={localStyles.mappedToolItem} data-testid={`unbound-tool-${type}`}>
      <div className={classNames(localStyles.mappedToolHeader, localStyles.unboundToolHeader)}>
        <div className={localStyles.unboundToolText}>
          <span className={localStyles.mappedToolName}>
            <ToolIcon tool={tool} size="medium" />
            {toolName}
          </span>
          <span className={localStyles.projectKeyText}>{getUnboundDescription(type, repositoryType)}</span>
        </div>
        <GhostButton
          type="default"
          size="small"
          state={isMapFormOpen ? 'disabled' : 'default'}
          onClick={() => onMap(type)}
          ariaLabel={`Map ${toolName}`}
        >
          Map
        </GhostButton>
      </div>
    </div>
  )
}

type ActionButtonsProps = {
  onOpenDeleteRepositoryDialog: () => unknown
  onCloseDrawer: () => unknown
}

const ActionButtons: React.FC<ActionButtonsProps> = ({ onOpenDeleteRepositoryDialog, onCloseDrawer }) => {
  return (
    <>
      <GhostButton icon={<TrashSimpleIcon size={16} />} type="destructive" onClick={onOpenDeleteRepositoryDialog}>
        Delete repository
      </GhostButton>
      <div style={{ marginLeft: 'auto' }}>
        <GhostButton type="secondary" onClick={onCloseDrawer}>
          Close
        </GhostButton>
      </div>
    </>
  )
}
